// ─── clinicalClearance.js ───────────────────────────────────────────────
// P1.2 (remède sécurité clinique, 2026-06-10) — clairance clinique unique.
//
// Un plan généré sort par QUATRE portes (Adopter, export Word, export Fiche
// frigo, publication app cliente). Avant remède, le gate (cosmétique) vivait
// sur un seul bouton → trois backdoors le contournaient. Ici le gate vit dans
// UNE fonction pure consultée par les quatre portes.
//
// HYPOTHÈSE ARCHITECTURALE — porte « publication app » (2026-06-13) :
// Cette clairance protège la publication TANT QUE le SaaS reste le SEUL
// détenteur du Bearer admin (ADMIN_INVITE_SECRET) de l'app cliente ET passe
// TOUJOURS par ce gate avant POST /api/admin/publish-plan. Vérifié à cette
// date : l'app cliente est purement consultative sur le plan (routes
// client/plan/* en GET seul, aucune (re)génération côté cliente ; les routes IA
// admin ne fabriquent pas de plan). L'endpoint publish-plan ne re-vérifie pas la
// clairance lui-même — il fait confiance à l'appelant SaaS. Ce n'est PAS un bug,
// c'est une CONDITION DE VALIDITÉ : si un jour l'app peut écrire/régénérer un
// plan, ou si un autre détenteur du Bearer apparaît, ce gate doit être dupliqué
// côté endpoint. À ne pas perdre.
//
// Sévérité INTRINSÈQUE au type de match (pas de colonne `severity` en DB — la
// gravité n'est pas un attribut de ligne guardrail, c'est la nature du match
// qui la porte) :
//   HIGH (bloquant, override conscient aux portes manuelles) :
//     - allergène déclaré (form.allergies) présent dans le plan
//     - phrase interdite guardrail présente dans le plan
//     - interaction classée 'blocking' présente dans le plan
//   LOW (warning non bloquant) :
//     - intolérance (form.alimentsEvites) présente
//     - interaction classée 'needs_review' présente (à valider Anissa)
//
// FAIL-CLOSED : input illisible ou audit qui throw = clairance refusée
// (cleared:false, severity:'high'). Jamais de feu vert par défaut. C'est le
// réflexe inverse du dev par défaut — il est explicite ici.
//
// La classification bloquant/advisory/needs_review est une DONNÉE révisable
// par Anissa (clinicalInteractions.js), pas une décision codée par un
// non-clinicien.

import { analyzeAnamnese } from './anamneseAnalyzer';
import { detectClientProfile } from './prompts/nutrition/profiles/_detector.fr';
import {
  detectClinicalGuardrails,
  auditPlanForGuardrails,
} from './prompts/nutrition/_clinicalGuardrails.fr';
import { classifyInteraction, normalizeForMatch } from './clinicalInteractions';
import {
  resolveMandatoryRiskFlags,
  findUnknownRiskFlags,
  riskFlagLabelFr,
} from './riskProfileFlags';
import { hasDeclaredRenalImpairment } from './prompts/nutrition/profiles/_detector.fr';
import { assertMgdClinicalPolicyReady } from './clinicalPolicyRegistry';
import { readCurrentTreatments } from './clinical/currentTreatments';

/**
 * @typedef {object} ClearanceVerdict
 * @property {boolean} cleared - false si une violation HIGH (ou fail-closed).
 * @property {'none'|'high'} severity - gravité maximale rencontrée.
 * @property {Array<{type:string, severity:'high', label:string, snippet?:string}>} violations
 * @property {Array<{type:string, severity:'low', label:string}>} warnings
 */

const FAIL_CLOSED = Object.freeze({
  cleared: false,
  severity: 'high',
  violations: [{ type: 'clearance_error', severity: 'high', label: 'Clairance impossible (entrée illisible ou audit en échec) — bloqué par sécurité.' }],
  warnings: [],
});

/** Découpe un champ libre (allergies/intolérances) en tokens significatifs. */
const EMPTY_DECLARATION_SENTINELS = new Set(['aucun', 'aucune', 'neant', 'ras']);

// Normalisation locale au matching allergènes/intolérances : accents retirés
// (normalizeForMatch), ligatures dépliées (« œufs » doit matcher « oeufs »),
// apostrophes ramenées à un espace (« d’arachide » → « d arachide »).
function normalizeAllergenText(s) {
  return normalizeForMatch(s)
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/[’'ʼ`´]/g, ' ');
}

// Découpe aussi les parenthèses/crochets : « Nickel (cacao, noix) » donne
// nickel, cacao, noix — pas « nickel (cacao » ni « ) ». Un token sans au moins
// trois lettres (ponctuation seule, « ok », « a ») est ignoré.
export function tokenize(field) {
  return String(field ?? '')
    .split(/[,;/\n()[\]{}]| et /i)
    .map((t) => normalizeAllergenText(t)
      .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '')
      .replace(/\s+/g, ' '))
    .filter((t) => (t.match(/[a-z]/g) || []).length >= 3
      && !EMPTY_DECLARATION_SENTINELS.has(t));
}

// Contexte d'exclusion (décision produit 2026-10-11, resserré après revue
// clinique le même jour). Une occurrence d'un allergène déclaré n'est ignorée
// QUE dans une formulation fermée et collée à l'allergène :
//   « sans X », « pas de X », « aucun X », « zéro X », « éviter (le) X »,
//   « éviction du X », « exclure (le) X », « allergie / allergique /
//   intolérance au X », ou « X : exclu / interdit / proscrit ».
// et seulement si la proposition se FERME juste après (ponctuation, fin de
// ligne, ou « et/ni/ou » suivi d'une autre exclusion). Pas d'héritage de liste,
// pas de « pauvre en » / « faible en » (peu ≠ zéro : décision d'Anissa), et
// toute tournure qui retourne ou relativise le sens (« jamais sans », « pas un
// jour sans », « n'oublie pas », « inutile d'éviter », « le soir »…) laisse
// l'alerte. Dans le doute, on signale (fail-closed) ; Anissa garde la
// confirmation consciente.
const EXCLUSION_BEFORE = new RegExp(
  '(?:^|[^a-z0-9])(?:sans|ni|pas\\s+de|pas\\s+d|aucun|aucune|zero'
  + '|eviter|evitez|evite|exclure|excluez'
  + '|eviction(?:\\s+(?:du|de|des|d))?|exclusion(?:\\s+(?:du|de|des|d))?'
  + '|(?:allergie|allergies|allergique|allergiques|intolerance|intolerances|intolerant|intolerante)'
  + '\\s+(?:a|au|aux))'
  + '(?:\\s+(?:le|la|les|l|du|de|des|d))?\\s+$',
);
const EXCLUSION_AFTER = /^\s*:?\s*(?:a\s+eviter|exclu|exclue|exclus|exclues|interdit|interdite|interdits|interdites|proscrit|proscrite|proscrits|proscrites)(?![a-z0-9])/;
const CLAUSE_CLOSED = /^\s*(?:$|[,;:.!?)\]\n•|]|ni\s|(?:et|ou)\s+(?:sans|ni|pas|aucun|aucune|zero|eviter|evitez|evite|exclure)(?![a-z0-9]))/;
// Mots qui renversent ou relativisent l'exclusion dans la même proposition.
const EXCLUSION_REVERSERS = /(?:^|[^a-z0-9])(?:jamais|ne|n|besoin|inutile|oublie|oublier|oubliez|prive|priver|privez|jour|jours|sauf|excepte|plus|moins|exces|soir|matin|midi|parfois|limite|limiter|limitez|modere|moderer|moderation)(?![a-z0-9])/;
// Fin de phrase / de cellule : le contexte ne traverse jamais ces signes.
const SENTENCE_BOUNDARY = /[.;!?\n•|]/;

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function sentenceBounds(normText, start, end) {
  let sentenceStart = start;
  while (sentenceStart > 0 && !SENTENCE_BOUNDARY.test(normText[sentenceStart - 1])) sentenceStart -= 1;
  let sentenceEnd = end;
  while (sentenceEnd < normText.length && !SENTENCE_BOUNDARY.test(normText[sentenceEnd])) sentenceEnd += 1;
  return [sentenceStart, sentenceEnd];
}

function isExcludedOccurrence(normText, start, end) {
  const [sentenceStart, sentenceEnd] = sentenceBounds(normText, start, end);
  const prefix = normText.slice(sentenceStart, start);
  const suffix = normText.slice(end, sentenceEnd);
  if (EXCLUSION_REVERSERS.test(prefix) || EXCLUSION_REVERSERS.test(suffix)) return false;
  if (EXCLUSION_BEFORE.test(prefix)) return CLAUSE_CLOSED.test(suffix);
  const after = suffix.match(EXCLUSION_AFTER);
  if (after) return CLAUSE_CLOSED.test(suffix.slice(after[0].length));
  return false;
}

/**
 * Tokens déclarés réellement PRÉSENTS dans le texte (hors formulation
 * d'exclusion fermée). Match en début de mot sur le texte normalisé — « oeuf »
 * ne matche plus « boeuf » — avec suffixe de flexion admis (« arachides »,
 * « laitiers ») pour ne rien perdre par rapport à l'ancien match brut.
 */
export function findDeclaredTokensPresent(text, field) {
  const tokens = tokenize(field);
  if (tokens.length === 0) return [];
  const normText = normalizeAllergenText(text);
  const present = [];
  for (const tok of tokens) {
    const pattern = new RegExp(
      `(?<![a-z0-9])${tok.split(' ').map(escapeRegExp).join('\\s+')}[a-z0-9]*`,
      'g',
    );
    for (const m of normText.matchAll(pattern)) {
      if (!isExcludedOccurrence(normText, m.index, m.index + m[0].length)) {
        present.push(tok);
        break;
      }
    }
  }
  return present;
}

// Un mot d'aliment, singulier ou pluriel : « arachide(s) », « oeuf(s) »,
// « noix ». Même tolérance que la comparaison des menus (sameExplicitFood).
function foodWordPattern(word) {
  const base = word.length > 3 && /[sx]$/.test(word) ? word.slice(0, -1) : word;
  return `${escapeRegExp(base)}[sx]?`;
}

/**
 * Variante de findDeclaredTokensPresent pour des termes DÉJÀ normalisés
 * (explicitFoodExclusions : habillage « sans », « allergie aux » retiré,
 * listes « et/ou » séparées). Tolère singulier/pluriel et ligatures, puis
 * applique la même règle d'éviction fermée (« sans arachides, … » n'est pas
 * une présence). Les appelants historiques de findDeclaredTokensPresent ne
 * sont pas modifiés.
 *
 * @param {string} text
 * @param {string[]} terms - termes normalisés (minuscules, sans accents)
 * @returns {string[]} termes réellement présents
 */
export function findExclusionTermsPresent(text, terms) {
  const normText = normalizeAllergenText(text);
  const present = [];
  for (const term of terms || []) {
    const words = String(term || "").split(/\s+/).filter(Boolean);
    if (words.length === 0) continue;
    const pattern = new RegExp(
      `(?<![a-z0-9])${words.map(foodWordPattern).join('\\s+')}(?![a-z0-9])`,
      'g',
    );
    for (const m of normText.matchAll(pattern)) {
      if (!isExcludedOccurrence(normText, m.index, m.index + m[0].length)) {
        present.push(term);
        break;
      }
    }
  }
  return present;
}

function sameComponentHashes(actual, expected) {
  if (!actual || typeof actual !== 'object' || Array.isArray(actual)
      || !expected || typeof expected !== 'object' || Array.isArray(expected)) return false;
  const actualKeys = Object.keys(actual).sort();
  const expectedKeys = Object.keys(expected).sort();
  return actualKeys.length === expectedKeys.length
    && actualKeys.every((key, index) => key === expectedKeys[index]
      && actual[key] === expected[key]);
}

/**
 * Le navigateur ne réexécute aucune décision clinique pour MGD. Il accepte
 * uniquement le verdict serveur lié au texte exact et aux six empreintes du
 * registre scellé. Une preuve absente/incomplète provoque une exception, puis
 * le catch de la clairance ferme le passage.
 */
function readMgdServerClearance(planText, clinicalContext, manifest) {
  const evaluation = clinicalContext.mgdRegistryEvaluation;
  const verdict = evaluation?.verdict;
  if (!evaluation || typeof evaluation !== 'object' || Array.isArray(evaluation)
      || evaluation.source !== 'server_sealed_registry_v1'
      || evaluation.registry_key !== manifest.registryKey
      || evaluation.policy_version !== manifest.policyVersion
      || evaluation.manifest_sha256 !== manifest.manifestSha256
      || evaluation.registry_definition_sha256 !== manifest.registryDefinitionSha256
      || evaluation.plan_text !== planText
      || !sameComponentHashes(evaluation.component_hashes, manifest.componentHashes)
      || !verdict || typeof verdict !== 'object' || Array.isArray(verdict)
      || typeof verdict.cleared !== 'boolean'
      || !['none', 'high'].includes(verdict.severity)
      || !Array.isArray(verdict.violations) || !Array.isArray(verdict.warnings)
      || verdict.violations.some((item) => !item || typeof item !== 'object'
        || item.severity !== 'high' || typeof item.type !== 'string'
        || typeof item.label !== 'string')
      || verdict.warnings.some((item) => !item || typeof item !== 'object'
        || item.severity !== 'low' || typeof item.type !== 'string'
        || typeof item.label !== 'string')
      || verdict.cleared !== (verdict.violations.length === 0)
      || verdict.severity !== (verdict.cleared ? 'none' : 'high')) {
    throw new Error('mgd_registry_evaluation_missing');
  }
  return {
    cleared: verdict.cleared,
    severity: verdict.severity,
    violations: verdict.violations.map((item) => ({ ...item })),
    warnings: verdict.warnings.map((item) => ({ ...item })),
  };
}

/**
 * Classifieur d'âge TRI-ÉTAT (mineur / majeur / inconnu) pour le blocage
 * PROFILE-based de la clairance. Distinct des checks CONTENT-based (qui lisent
 * le plan) : celui-ci lit l'âge déclaré du dossier, peu importe le texte du plan.
 *
 * Pourquoi tri-état et pas le binaire `age > 0 && age < 18` du guardrail
 * `adolescente` : ce dernier mélange silencieusement l'âge inconnu avec « pas
 * mineure » (age=0 issu d'un champ vide en est le piège classique). Ici on veut
 * un 3e état explicite pour AVERTIR (fail-open) sans bloquer quand l'âge manque.
 *
 * Lit `form.age` puis `form.ageActuel` (même convention que detectClinicalGuardrails).
 *
 *   'minor'   : entier 1..17   → blocage HIGH (validation obligatoire)
 *   'adult'   : entier 18..120 → rien
 *   'unknown' : vide/null/NaN/≤0/>120 → warning LOW (fail-open, jamais bloquant)
 *
 * Pure, fail-safe.
 *
 * @param {object|null|undefined} form
 * @returns {'minor'|'adult'|'unknown'}
 */
export function classifyAge(form, now = new Date()) {
  if (!form || typeof form !== 'object') return 'unknown';
  let age = null;
  const primaryBirthDate = typeof form.dateNaissance === 'string'
    ? form.dateNaissance.trim()
    : form.dateNaissance;
  const aliasBirthDate = typeof form.date_naissance === 'string'
    ? form.date_naissance.trim()
    : form.date_naissance;
  if (primaryBirthDate && aliasBirthDate && primaryBirthDate !== aliasBirthDate) {
    return 'unknown';
  }
  const rawBirthDate = primaryBirthDate || aliasBirthDate;
  const match = typeof rawBirthDate === 'string'
    ? rawBirthDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
    : null;
  if (match && now instanceof Date && !Number.isNaN(now.getTime())) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const candidate = new Date(Date.UTC(year, month - 1, day));
    if (candidate.getUTCFullYear() === year
        && candidate.getUTCMonth() === month - 1
        && candidate.getUTCDate() === day) {
      age = now.getUTCFullYear() - year;
      const birthdayPending = now.getUTCMonth() < month - 1
        || (now.getUTCMonth() === month - 1 && now.getUTCDate() < day);
      if (birthdayPending) age -= 1;
    }
  }
  if (age === null) {
    const primaryAge = typeof form.age === 'string' ? form.age.trim() : form.age;
    const aliasAge = typeof form.ageActuel === 'string' ? form.ageActuel.trim() : form.ageActuel;
    const rawAge = primaryAge === '' || primaryAge == null ? aliasAge : primaryAge;
    const recorded = Number.parseInt(rawAge ?? '', 10);
    age = Number.isFinite(recorded) ? recorded : null;
  }
  if (!Number.isFinite(age)) return 'unknown'; // '', null, undefined, 'abc'
  if (age <= 0) return 'unknown';              // 0 (champ vide parsé), négatifs
  if (age > 120) return 'unknown';             // valeurs absurdes
  if (age < 18) return 'minor';                // 1..17
  return 'adult';                              // 18..120
}

// V97.61 — corticoïdes : ne bloquer que l'usage SYSTÉMIQUE (décision Anissa
// 2026-07-02, point 5 : « une crème à la cortisone ne doit pas déclencher un
// blocage destiné aux traitements immunosuppresseurs systémiques »).
// Analyse PAR SEGMENT (découpe virgules/points-virgules/retours ligne) : un
// segment corticoïde sans marqueur topique = systémique. Ainsi « Prednisone
// 10mg, crème corticoïde » bloque (le 1er segment est systémique) alors que
// « crème corticoïde pour eczéma » seul ne bloque pas. La regex corticoïde
// est STRICTEMENT la même alternation que RE_CORTICOIDES (anamneseAnalyzer),
// aucun élargissement ; seuls les marqueurs topiques sont nouveaux — ils
// implémentent la décision d'Anissa, pas un choix clinique du dev.
const RE_CORTICOID_SEGMENT = /corticoide|prednisone|cortancyl|solupred|medrol/i;
const RE_TOPICAL_MARKER = /creme|cr[eè]me|pommade|topique|cutan|dermocortico|lotion|onguent|\bgel\b|\blocal/i;

/**
 * Usage corticoïde SYSTÉMIQUE mentionné dans les traitements. Pure, fail-safe.
 *
 * Jointure par '\n' (PAS un espace) : les deux champs ne doivent JAMAIS
 * fusionner en un seul segment, sinon une crème dans `traitements` désarmerait
 * une prednisone systémique dans `medicaments` (fail-open attrapé par la revue
 * clinique V97.61). Découpe alignée sur tokenize (virgules, points-virgules,
 * retours ligne, « et ») pour séparer aussi « prednisone orale et crème X ».
 *
 * @param {object|null|undefined} form
 * @returns {boolean}
 */
export function hasSystemicCorticoidUse(form) {
  const text = readCurrentTreatments(form).text;
  return text
    .split(/[,;\n]| et /i)
    .some((seg) => RE_CORTICOID_SEGMENT.test(seg) && !RE_TOPICAL_MARKER.test(seg));
}

/**
 * Clairance clinique d'un plan généré. Fonction pure, fail-closed.
 *
 * @param {string} planText - Le plan nutrition généré.
 * @param {{ form?: object, guardrails?: Array }} [clinicalContext]
 * @returns {ClearanceVerdict}
 */
export function assertPlanClinicallyCleared(planText, clinicalContext = {}) {
  // FAIL-CLOSED : pas de plan lisible = pas de clairance possible.
  if (typeof planText !== 'string' || planText.trim() === '') {
    return { ...FAIL_CLOSED, violations: [...FAIL_CLOSED.violations] };
  }

  try {
    const form = clinicalContext.form || {};
    const mgdPolicyManifest = clinicalContext.requireMgdPolicyRegistry === true
      ? assertMgdClinicalPolicyReady(clinicalContext.mgdPolicyManifest)
      : null;
    if (mgdPolicyManifest) {
      return readMgdServerClearance(planText, clinicalContext, mgdPolicyManifest);
    }
    const normPlan = normalizeForMatch(planText);
    const violations = [];
    const warnings = [];

    // 0. Profil mineur (PROFILE-based, ≠ des checks content-based ci-dessous :
    //    ne lit PAS le plan, lit l'âge déclaré). Décision Anissa 2026-06-13 :
    //    aucun plan pour une mineure ne sort sans validation consciente.
    //      mineure (1..17)      → HIGH bloquant (override conscient possible)
    //      âge inconnu/invalide → warning LOW (fail-open, jamais bloquant)
    //      majeure (18..120)    → rien
    //    Complémentaire (pas redondant) avec le guardrail `adolescente`, qui lui
    //    est content-based (mots interdits dans le plan) : ici on bloque même un
    //    plan « propre » de mots interdits, sur le seul statut mineur.
    //    Limite assumée : l'âge est DÉCLARATIF — protège les mineures déclarées,
    //    pas celles se déclarant majeures.
    const ageClass = classifyAge(form);
    if (ageClass === 'minor') {
      violations.push({ type: 'minor', severity: 'high', label: 'Cliente mineure (moins de 18 ans) — validation clinique obligatoire avant de sortir ce plan.' });
    } else if (ageClass === 'unknown') {
      warnings.push({ type: 'age_unknown', severity: 'low', label: 'Âge non renseigné ou invalide — vérification manuelle du statut mineur recommandée.' });
    }

    // 0bis. Profils à risque flaggés par la praticienne (V97.58, volet TCA de
    //    l'audit P4.7). PROFILE-based comme le check mineure : ne lit PAS le
    //    plan, lit les flags manuels de la fiche (form.flagsProfilRisque).
    //    Comportement « validation obligatoire » du doc VALIDATION-CLINIQUE-
    //    ANISSA-V1 : aucun plan pour un profil à risque ne sort sans validation
    //    consciente (override explicite aux portes manuelles). Le flag est posé
    //    par Anissa elle-même — le blocage est donc toujours voulu, jamais un
    //    faux positif de détection.
    // V97.61 : seuls les flags `mandatoryValidation: true` bloquent (arbitrage
    // Anissa 2026-07-02 — chirurgie récente injecte ses contraintes SANS
    // blocage, comportement B). La liste bloquante vit dans le registre.
    const mandatoryRiskFlags = resolveMandatoryRiskFlags(form);
    for (const flagId of mandatoryRiskFlags) {
      const label = riskFlagLabelFr(flagId);
      violations.push({ type: 'risk_profile', severity: 'high', label: `Profil à risque déclaré — ${label} : validation clinique obligatoire avant de sortir ce plan.` });
    }
    // FAIL-CLOSED (revue clinique V97.58) : un id inconnu dans
    // flagsProfilRisque = donnée de sécurité posée par la praticienne mais
    // illisible (registre renommé, DB corrompue). Elle ne produit aucune
    // contrainte de prompt → si on l'ignorait ici aussi, le flag disparaîtrait
    // en silence avec un feu vert. Doute = fermeture.
    const unknownRiskFlags = findUnknownRiskFlags(form);
    for (const unknownId of unknownRiskFlags) {
      violations.push({ type: 'risk_profile_unknown', severity: 'high', label: `Flag profil à risque non reconnu (« ${unknownId} ») — vérifier la fiche cliente avant de sortir ce plan.` });
    }

    // Analyses partagées par les checks suivants (une seule passe chacune).
    const anam = analyzeAnamnese(form);
    const profile = detectClientProfile(form);

    // 0ter. Profils « validation obligatoire » DÉTECTÉS depuis l'anamnèse
    //    (V97.59 — décision Anissa figée 2026-06-12, mapping #2/#3/#7).
    //    V97.61 — anti-faux-positifs, décision Anissa 2026-07-02 (point 5) :
    //      rénal   → seule une atteinte DÉCLARÉE bloque (hasDeclaredRenal-
    //                Impairment, regex explicite) — les suspicions heuristiques
    //                (T1 ancien, T1+complications) restent dans le tag composer
    //                pour l'injection de contenu, mais ne bloquent plus ;
    //      cortico → seul un usage SYSTÉMIQUE bloque (une crème/pommade
    //                mentionnée dans les traitements ne bloque pas).
    //    Le label dit la SOURCE (anamnèse) ; override conscient aux portes.
    const mandatoryProfiles = [];
    if (anam?.pathologies?.diabete_t1?.active) {
      mandatoryProfiles.push('Diabète de type 1');
    }
    if (hasDeclaredRenalImpairment(form)) {
      mandatoryProfiles.push('Insuffisance rénale / fonction rénale fragilisée');
    }
    if (anam?.traitements?.biotherapie?.active || hasSystemicCorticoidUse(form)) {
      mandatoryProfiles.push('Traitement immunosuppresseur (biothérapie / corticoïdes)');
    }
    for (const name of mandatoryProfiles) {
      violations.push({ type: 'mandatory_profile', severity: 'high', label: `Profil « ${name} » détecté depuis l'anamnèse — validation clinique obligatoire avant de sortir ce plan.` });
    }

    // 1. Allergènes déclarés présents dans le plan → HIGH.
    //    Une mention d'exclusion (« sans nickel ») n'est pas une présence.
    for (const tok of findDeclaredTokensPresent(planText, form.allergies)) {
      violations.push({ type: 'allergen', severity: 'high', label: `Allergène déclaré présent dans le plan : « ${tok} ».` });
    }

    // 2. Intolérances présentes → warning non bloquant (même matcher).
    for (const tok of findDeclaredTokensPresent(planText, form.alimentsEvites)) {
      warnings.push({ type: 'intolerance', severity: 'low', label: `Intolérance déclarée mentionnée dans le plan : « ${tok} » (à vérifier).` });
    }

    // 3. Phrases interdites guardrail présentes → HIGH.
    const guardrails = Array.isArray(clinicalContext.guardrails)
      ? clinicalContext.guardrails
      : detectClinicalGuardrails(profile, form);
    const grViolations = auditPlanForGuardrails(planText, guardrails);
    for (const gv of grViolations) {
      violations.push({ type: 'guardrail', severity: 'high', label: `Formulation interdite (${gv.profile_key}) : « ${gv.phrase} ».`, snippet: gv.snippet });
    }

    // 4. Interactions complément↔traitement présentes dans le plan.
    //    'blocking' → HIGH ; 'needs_review' → warning ; 'advisory' → ignoré.
    //    `treatments` est passé à classifyInteraction pour l'escalade
    //    conditionnelle (oméga-3 forte dose sous AVK/AOD, berbérine sous
    //    insuline/metformine) — réf. docs/VALIDATION-CLINIQUE-ANISSA-V1.md.
    const treatments = anam.traitements || {};
    for (const t of Object.values(treatments)) {
      if (!t || !t.active || !Array.isArray(t.interactions)) continue;
      for (const raw of t.interactions) {
        const cls = classifyInteraction(raw, treatments);
        if (cls === 'advisory') continue;
        const keyword = normalizeForMatch(String(raw).split('(')[0]);
        if (keyword.length < 3 || !normPlan.includes(keyword)) continue;
        if (cls === 'blocking') {
          violations.push({ type: 'interaction', severity: 'high', label: `Substance contre-indiquée avec un traitement en cours : « ${keyword} ».` });
        } else {
          warnings.push({ type: 'interaction_review', severity: 'low', label: `Substance à valider cliniquement (interaction possible) : « ${keyword} ».` });
        }
      }
    }

    const cleared = violations.length === 0;
    return {
      cleared,
      severity: cleared ? 'none' : 'high',
      violations,
      warnings,
    };
  } catch {
    // FAIL-CLOSED : tout audit qui throw = bloqué, jamais feu vert.
    return { ...FAIL_CLOSED, violations: [...FAIL_CLOSED.violations] };
  }
}

// P1.2 (colmatage backdoors, 2026-06-10) — erreur jetée par la garde d'export.
// Même rôle que PublishClinicalError côté publication : porte le verdict pour
// que l'UI puisse présenter un override conscient. Le gate vit dans le SERVICE
// d'export (assertExportCleared), pas sur les boutons — sinon chaque nouveau
// bouton qui appelle exportPlanToWord/exportFicheFrigoPDF rouvre une backdoor.
export class ExportClinicalError extends Error {
  constructor(verdict) {
    super('Clairance clinique refusée — export bloqué.');
    this.name = 'ExportClinicalError';
    this.verdict = verdict;
  }
}

/**
 * P1.2 — Garde de clairance pour les services d'export (Word, Fiche frigo PDF).
 * À appeler en TÊTE de chaque fonction d'export pour que tous les call sites
 * soient couverts par une seule barrière. Throw ExportClinicalError si le plan
 * n'est pas clairé, sauf override conscient explicite (options.clinicalOverride
 * positionné par l'UI après un window.confirm).
 *
 * Renvoie TOUJOURS le verdict (même en override) pour traçabilité.
 *
 * @param {string} planText
 * @param {{ form?: object, guardrails?: Array }} [clinicalContext]
 * @param {{ clinicalOverride?: boolean }} [options]
 * @returns {ClearanceVerdict}
 */
export function assertExportCleared(planText, clinicalContext = {}, options = {}) {
  const verdict = assertPlanClinicallyCleared(planText, clinicalContext);
  if (!verdict.cleared && !options.clinicalOverride) {
    throw new ExportClinicalError(verdict);
  }
  return verdict;
}

/**
 * Formate un verdict pour un override conscient (window.confirm aux portes
 * manuelles). Pure — testable. Renvoie le message à présenter à Anissa.
 *
 * @param {ClearanceVerdict} verdict
 * @returns {string}
 */
export function formatClearanceForConfirm(verdict) {
  const v = verdict || {};
  const lines = ['Clairance clinique — ce plan présente un risque de sécurité :', ''];
  for (const x of v.violations || []) lines.push(`• ${x.label}`);
  if (Array.isArray(v.warnings) && v.warnings.length > 0) {
    lines.push('', 'À vérifier (non bloquant) :');
    for (const w of v.warnings) lines.push(`• ${w.label}`);
  }
  lines.push('', 'Sortir ce plan malgré cet avertissement ? (override conscient)');
  return lines.join('\n');
}

/**
 * P1.3 — Traduit un verdict (frais OU relu depuis trigger_metadata, parfois
 * absent sur d'anciens brouillons pré-P1.3) en descripteur d'affichage pour le
 * panel de validation. Le badge est INFORMATIF : le gate dur reste la
 * re-vérification live à la publication (assertPlanClinicallyCleared dans
 * publishConsultationToClientApp).
 *
 * @param {ClearanceVerdict|null|undefined} verdict
 * @returns {{ tone: 'block'|'warn'|'ok'|'unknown', label: string, blocking: boolean }}
 */
export function clearanceBadge(verdict) {
  if (!verdict || typeof verdict !== 'object') {
    return { tone: 'unknown', label: 'Clairance non évaluée', blocking: false };
  }
  const violations = Array.isArray(verdict.violations) ? verdict.violations : [];
  const warnings = Array.isArray(verdict.warnings) ? verdict.warnings : [];
  if (verdict.cleared === false || verdict.severity === 'high' || violations.length > 0) {
    const n = violations.length;
    return {
      tone: 'block',
      label: n > 0
        ? `Clairance refusée — ${n} alerte${n > 1 ? 's' : ''} bloquante${n > 1 ? 's' : ''}`
        : 'Clairance refusée',
      blocking: true,
    };
  }
  if (warnings.length > 0) {
    const n = warnings.length;
    return { tone: 'warn', label: `À vérifier — ${n} avertissement${n > 1 ? 's' : ''}`, blocking: false };
  }
  return { tone: 'ok', label: 'Clairance clinique OK', blocking: false };
}
